import { SelectOptions } from "@/Components/FastExtras/Garnishes";
import i18n from "@/i18n";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/nl";
import "dayjs/locale/tr";
import { UseFormMethods } from "react-hook-form";
import { toast } from "react-toastify";
import { PaginationStatusProps } from "../atoms/useOrderPageAtom";
import { getLibraryImageUrl, isAiImage } from "../imageLibrary/aiImage";
import { IMAGE_LIBRARY_TYPE, ImageLibraryType } from "../imageLibrary/types";
import {
  CourierReport,
  OrderInformation,
  OrderV2,
} from "../v2/queries/interfacesV2";
import {
  MAX_ORDER_COUNT_FOR_ARRIVED,
  MAX_ORDER_COUNT_FOR_OTHERS,
  deliveryMethods,
  statussen,
} from "./constant";
import {
  CustomDiscountType,
  CountryCode,
  CategoryType,
  DeliveryMethods,
  OrderStatus,
  SortBy,
  Sources,
} from "./enum";
import {
  Branch,
  CartContent,
  CartItem,
  Category,
  CmsUsersResponse,
  DataProperty,
  DataStructure,
  DataValuaStructure,
  Deposit,
  Garnish,
  MenuDataTagBody,
  Product,
  Schedule,
  Topping,
  ZipcodesTableDataRequest,
} from "./interfaces";
import { countryConfig } from "./countryConfig";
import { codeToCountry } from "./countries";
export const guidGenerator = () => {
  var S4 = function () {
    return (((1 + Math.random()) * 0x10000) | 0).toString(8).substring(1);
  };
  return S4() + S4();
};

export const codeGenerator = () => {
  return (Math.random() + 1).toString(36).substring(7);
};

export const reorder = <T>(list: T[], startIndex, endIndex): T[] => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result;
};

export const getListStyle = (isDraggingOver) => ({
  background: isDraggingOver ? "lightblue" : "transparent",
  width: "100%",
});

export const getItemStyle = (isDragging, draggableStyle) => ({
  userSelect: "none",
  background: isDragging ? "transparent" : "transparent",
  ...draggableStyle,
});

export const errorToast = (message = "errors.create", toastId = undefined) => {
  const res = i18n.t(message);
  return toast.error(res, {
    style: { visibility: !message ? "hidden" : "visible" },
    toastId: toastId,
    position: "bottom-right",
    autoClose: 5000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: false,
    draggable: true,
    progress: undefined,
  });
};

export const getLabels = (products: Product[]) => {
  if (products?.length > 0) {
    const labels = new Set<string>();
    products.forEach((product) => {
      asArray(product?.label)?.forEach((l) =>
        l.trim().length > 0 ? labels.add(l) : null
      );
    });
    return Array.from(labels).map((label) => ({ value: label, label }));
  }
  return [];
};

export const toppingContentTypeOptions = [
  { value: 1, label: "selectRequired" },
  { value: 2, label: "selectOptional" },
  { value: 3, label: "checkbox" },
  /* 
   these will be used in future
  { value: 11, label: "text" },
  { value: 12, label: "date" },
  { value: 13, label: "time" },
  { value: 14, label: "datetime" }, 
  */
];

export const menuItemTypes = [
  { value: 0, label: "image_and_text" },
  { value: 1, label: "only_text" },
  { value: 2, label: "only_image" },
];

export const getValue = (param: any) => {
  if (param !== undefined) {
    const lang = "all";
    const value = param?.[0]?.[lang]?.value ?? param?.[0]?.[lang];
    return value === undefined ? param : value;
  }

  return "";
};

export const getInnerValue = (param: any) => {
  if (param !== undefined) {
    const lang = "all";
    const value = param?.[lang]?.value;
    return value === undefined ? param : value;
  }
  return "";
};

export const getValueByAll = (param: any[]) => {
  if (param) {
    const lang = "all";
    const value = param?.[0]?.[lang];
    return value === undefined ? param : value;
  }
  return "";
};

export const getValueByAllInArray = (param: any[]) => {
  if (param) {
    const lang = "all";
    const value = param?.map((p) => p?.[lang]);
    return value === undefined ? param : value;
  }
  return "";
};

export const getValuesInArray = (param: any[]) => {
  if (param) {
    const lang = "all";
    return param.map((p) => p?.[lang]?.["value"]);
  }
  return [];
};

export const formatArrayOfObject = <T = any[]>(data: DataStructure<T>[]) => {
  const res = data
    .map((object) => formatObject<T>(object))
    .map((data) => {
      return typeof data === "object"
        ? data?.all !== undefined
          ? data?.all
          : data
        : data;
    });
  return res as T[];
};

export const getValueFromObject = <T = any>(data: DataValuaStructure<T>) => {
  return data?.value === undefined ? data : data.value;
};
export function formatResponse<T>(data: T): T;
export function formatResponse<T = any>(
  data: DataStructure<T> | DataStructure<T>[]
) {
  if (Array.isArray(data)) {
    return formatArrayOfObject<T>(data);
  } else {
    return formatObject<T>(data);
  }
}

export const formatObject = <T = any>(
  object: DataStructure<T>,
  lang = "all"
) => {
  let obj = {};
  Object.keys(object).forEach((key) => {
    if (typeof object[key] !== "object" && !Array.isArray(object[key])) {
      return (obj[key] = object[key]);
    }
    obj[key] = getDynamicValue<T>(object[key], lang);
  });
  return obj as {
    [key: string]: T;
  };
};

export const getDynamicValue = <T = any>(
  data: DataProperty<T>,
  lang = "all"
) => {
  if (Array.isArray(data)) {
    if (!data.some((prop) => typeof prop === "object")) return data;
    if (data.length === 0 || data === undefined) return data;
    if (data.length === 1) {
      const object = data?.[0];

      const keys = Object.keys(object).length;
      if (keys === 1) {
        const objectValue = object?.[lang];
        if (typeof objectValue === "object") {
          return objectValue?.value ?? objectValue?.[0];
        } else {
          return objectValue;
        }
      } else {
        return formatObject<T>(object);
      }
    } else {
      return formatArrayOfObject<T>(data);
    }
  }
  if (typeof data === "object") {
    return getValueFromObject(data);
  }
  return data;
};

export const getFullStreetV2 = (order: OrderV2) =>
  `${order?.order_user?.street_name} ${order?.order_user?.house_number} ${order?.order_user?.house_number_extension}`
    ?.replaceAll("undefined", "")
    ?.trim();

export const getUserAddress = (user: any) =>
  `${user.zip_code} ${user.street_name} ${user.house_number} ${user.house_number_extension}`
    ?.replaceAll("undefined", "")
    ?.trim();

export const getFullAddressV2 = (order: OrderV2) =>
  `${order?.order_user?.street_name} ${order?.order_user?.house_number} ${order?.order_user?.house_number_extension} ${order?.order_user?.zip_code} ${order?.order_user?.city}`
    ?.replaceAll("undefined", "")
    ?.trim();

export const getStatusBy = (value: string | number) => {
  return (
    statussen.find((option) => option.value.includes(parseInt(`${value}`)))
      ?.translationKey || value
  );
};

export const getDeliveryBy = (value: number) => {
  return (
    deliveryMethods.find((option) => option.value === value)?.translationKey ||
    value
  );
};

export const IsJsonString = (str) => {
  try {
    JSON.parse(str);
  } catch (e) {
    return false;
  }
  return true;
};

export const capitalizeFirstLetter = (string: string) => {
  return string[0].toUpperCase() + string.slice(1);
};

export const groupArrayOfObjects = (list, key) => {
  return list.reduce(function (rv, x) {
    (rv[x[key]] = rv[x[key]] || []).push({ ...x, [key]: undefined });
    return rv;
  }, {});
};

export const ObjectByString = function (o: any, s: string, empty = {}): any {
  s = s.replace(/\[(\w+)\]/g, ".$1"); // convert indexes to properties
  s = s.replace(/^\./, ""); // strip a leading dot
  var a = s.split(".");
  for (var i = 0, n = a.length; i < n; ++i) {
    var k = a[i];
    if (k in o) {
      o = o?.[k] || i;
    } else {
      return empty;
    }
  }
  return o;
};

export const objectIsEmpty = (obj: {} = {}) => Object.keys(obj).length === 0;

const getActiveCountryConfig = () => {
  const countryCode =
    typeof localStorage === "undefined"
      ? CountryCode.NETHERLANDS
      : Number.parseInt(localStorage.getItem("country") ?? "", 10);
  const country = codeToCountry[countryCode] ?? "NETHERLANDS";

  return countryConfig[country];
};

export type DateFormatPreset =
  | "date"
  | "dateTime"
  | "dateTimeWithSeconds"
  | "dateTimeLocal"
  | "dateTimeLocalWithSeconds"
  | "dayOfMonth"
  | "inputDate"
  | "inputDateTime"
  | "inputDateTimeWithSeconds"
  | "longDate"
  | "longDateTime"
  | "monthYear"
  | "time"
  | "timeWithSeconds"
  | "weekdayShort";

type DateValue = string | number | Date | Dayjs | null | undefined;

/**
 * Formats dates through shared presets. Display presets use the active country's
 * convention; input presets preserve the machine formats required by native inputs.
 */
export const formatDate = (
  value: DateValue,
  preset: DateFormatPreset = "date"
) => {
  if (
    value === null ||
    value === undefined ||
    (typeof value === "string" && value.trim() === "")
  ) {
    return "-";
  }

  const date = dayjs(value);
  if (!date.isValid()) return "-";

  const { dateFormat, locale } = getActiveCountryConfig();
  const formats: Record<DateFormatPreset, string> = {
    date: dateFormat,
    dateTime: `${dateFormat} HH:mm`,
    dateTimeWithSeconds: `${dateFormat} HH:mm:ss`,
    dateTimeLocal: "YYYY-MM-DDTHH:mm",
    dateTimeLocalWithSeconds: "YYYY-MM-DDTHH:mm:ss",
    dayOfMonth: "D",
    inputDate: "YYYY-MM-DD",
    inputDateTime: "YYYY-MM-DD HH:mm",
    inputDateTimeWithSeconds: "YYYY-MM-DD HH:mm:ss",
    longDate: "D MMMM YYYY",
    longDateTime: "D MMMM YYYY HH:mm",
    monthYear: "MMMM YYYY",
    time: "HH:mm",
    timeWithSeconds: "HH:mm:ss",
    weekdayShort: "ddd",
  };

  return date.locale(locale).format(formats[preset]);
};

export const formatPrice = (price: number | string) => {
  const { locale, currency } = getActiveCountryConfig();

  const formatter = Intl.NumberFormat(locale, {
    style: "currency",
    currency: currency.code,
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  });

  const numericAmount =
    typeof price === "string" ? Number(price.replace(",", ".")) : price;

  if (isNaN(numericAmount)) return "-";

  if (Boolean(currency.hasDifferentSymbol) === false) {
    return formatter.format(numericAmount);
  }

  const parts = formatter.formatToParts(numericAmount);

  const numberPart = parts
    .filter((part) => part.type !== "currency")
    .map((part) => part.value)
    .join("")
    .trim();

  return currency.position === "before"
    ? `${currency.symbol} ${numberPart}`
    : `${numberPart} ${currency.symbol}`;
};

export const maxMonthFilterDate = formatDate(
  dayjs().add(2, "month"),
  "inputDate"
);

export const allergenen = [
  { name: "Ei", id: 3, translationKey: "egg" },
  { name: "Gluten", id: 1, translationKey: "gluten" },
  { name: "Lupine", id: 13, translationKey: "lupine" },
  { name: "Melk", id: 7, translationKey: "milk" },
  { name: "Mosterd", id: 10, translationKey: "mustard" },
  { name: "Noten", id: 8, translationKey: "nuts" },
  { name: "Pindas", id: 5, translationKey: "peanuts" },
  { name: "Schaald", id: 2, translationKey: "scale" },
  { name: "Selderij", id: 9, translationKey: "celery" },
  { name: "Sesamzaad", id: 11, translationKey: "sesameSeed" },
  { name: "Soja", id: 6, translationKey: "soy" },
  { name: "Vis", id: 4, translationKey: "fish" },
  { name: "Weekdieren", id: 14, translationKey: "mollus" },
  { name: "Zwavel", id: 12, translationKey: "sulphur" },
  { name: "Tarwe", id: 15, translationKey: "wheat" },
];

export const getFormData = (file: File, signData: any) => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("api_key", signData.api_key);
  formData.append("timestamp", signData.timestamp);
  formData.append("signature", signData.signature);
  formData.append("public_id", signData.public_id);
  return formData;
};

export const getMenuImageUrl = (
  image_cloudinary_id: string,
  branch: number,
  /** Library folder of AI images, which are linked instead of uploaded. */
  libraryType: ImageLibraryType = IMAGE_LIBRARY_TYPE.product
) => {
  if (!image_cloudinary_id) return null;
  if (isAiImage(image_cloudinary_id)) {
    return getLibraryImageUrl(image_cloudinary_id, libraryType);
  }
  let version: string = "";
  let fileId: string = "";

  if (image_cloudinary_id.includes("/")) {
    [version, fileId] = image_cloudinary_id?.split("/");
  }
  if (version) {
    version = version.startsWith("v") ? version.slice(1) : version;
    return `${
      import.meta.env.VITE_CLOUDINARY_BASE_URL
    }/v${version}/cms/menu_item/${branch}/${fileId}`;
  } else {
    return `${
      import.meta.env.VITE_CLOUDINARY_BASE_URL
    }/cms/menu_item/${branch}/${image_cloudinary_id}`;
  }
};

export const getBigAndSmallCartItemByContent = (items: CartItem[]) => {
  const small = items.reduce(function (prev, curr) {
    return (prev?.selected_sub_content?.length || 0) <
      (curr?.selected_sub_content?.length || 0)
      ? prev
      : curr;
  });

  const big = items.reduce(function (prev, curr) {
    return (prev?.selected_sub_content?.length || 0) >=
      (curr?.selected_sub_content?.length || 0)
      ? prev
      : curr;
  });

  return { small, big };
};

export const split = (str: string, index: number) => {
  const result = [str.slice(0, index), str.slice(index)];
  return result;
};

export function asArray<T>(value: T | T[] | undefined): T[] {
  return value === undefined ? [] : Array.isArray(value) ? value : [value];
}

export const beautifyStrings = (data: string[]) =>
  data.map((x, i, array) => `${x}${i === array.length ? "" : " "}`);

export const convertNumericBooleanToBoolean = (value: string | number) => {
  const trueValues = [1, "1"];
  const falseValues = [0, "0"];
  if (trueValues.includes(value)) return true;
  if (falseValues.includes(value)) return false;
  return value;
};

export const filterEmptyStringFromArray = (value: string[]) =>
  value.filter((v) => v !== "");

export const isObject = (val: any) =>
  val !== null &&
  val !== undefined &&
  !Array.isArray(val) &&
  typeof val === "object";

export const getFieldByLang = (
  field: string | Record<string, string>,
  desiredLang?: string
) => {
  const lang = desiredLang || "all";
  return isObject(field) ? field?.[lang] : field;
};

export const filterMenuItemByName = (
  name: string | Record<string, string>,
  search: string
) => {
  if (!search) return true;
  return isObject(name)
    ? Object.values(name || {})
        .join("")
        .toLocaleLowerCase()
        .includes(search.toLowerCase())
    : typeof name === "string"
      ? name?.toLowerCase().includes(search.toLowerCase())
      : true;
};

export const convertNullToUndefinedInObject = <T = any>(object: T) => {
  return Object.keys(object).reduce((acc, cur) => {
    acc[cur] = object[cur] ?? undefined;
    return acc;
  }, {}) as T;
};

export function getEnumKeyByEnumValue(
  myEnum: any,
  enumValue: number | string
): string {
  let keys = Object.keys(myEnum).filter((x) => myEnum[x] == enumValue);
  return keys.length > 0 ? keys[0] : "";
}

export const getCustomDiscountFromCartContent = (
  cartContent: Partial<CartContent>[]
) => {
  const customDiscountNames = [
    "(kassa vaste prijs korting)",
    "(kassa percentage korting)",
  ];

  const customDiscount = cartContent.find((item) =>
    customDiscountNames.some((name) => item.name.includes(name))
  );

  if (customDiscount) {
    const isPercentageDiscount = customDiscount.name.includes(
      "(kassa percentage korting)"
    );
    const percentage = customDiscount.name.split("%")[0];
    return {
      [isPercentageDiscount ? "percentage_discount" : "fixed_price_discount"]:
        isPercentageDiscount
          ? percentage
          : parseFloat(customDiscount.single_price_inc_vat) * -1,
      [isPercentageDiscount
        ? "percentage_discount_name"
        : "fixed_price_discount_name"]: customDiscount.name,
      type: isPercentageDiscount
        ? CustomDiscountType.PERCENTAGE
        : CustomDiscountType.FIXED_PRICE,
    };
  }
};

export const getDayWithTimeFormat = (date?: string) =>
  date ? formatDate(date, "inputDateTimeWithSeconds") : undefined;

export const getBaseZipcode = (zipcode: string) =>
  zipcode.length === 6 ? zipcode.slice(0, 4) : zipcode;

export function hexToRGBA(hex: string, alpha = 1) {
  // Verwijder eventueel een leading #
  hex = hex.replace(/^#/, "");

  // Korte notatie (#FFF) omzetten naar lange (#FFFFFF)
  if (hex.length === 3) {
    hex = hex
      .split("")
      .map((c) => c + c)
      .join("");
  }

  const bigint = parseInt(hex, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;

  return [r, g, b, alpha];
}

export const optionsToValues = (values: SelectOptions[]) =>
  values.map((v) => v.value);

export const filterOrdersBySearch = (
  orders: OrderV2[],
  search: string,
  couriers: CmsUsersResponse[] = []
) => {
  return orders?.filter((order) => {
    let user = Object.values(order.order_user).map((value) => String(value));
    const { remarks, item_id, public_order_number, daily_receipt_number } =
      order.order_information;
    user.push(String(item_id));
    user.push(String(public_order_number));
    user.push(getFullStreetV2(order));
    if (search.startsWith("#")) {
      return search.length > 1 ? `#${daily_receipt_number}` === search : true;
    }

    if (search.startsWith("@")) {
      if (order.order_information.assigned_courier_id) {
        const c = couriers.find(
          (c) => c.item_id === order.order_information.assigned_courier_id
        );
        if (c) {
          return search.length > 1
            ? `@${c.name.toLowerCase()}`.includes(search.toLowerCase())
            : true;
        }
        return false;
      }
      return false;
    }

    if (search.startsWith("!")) {
      return search.length > 1
        ? `!${remarks}`
            .replace(/\s/g, "")
            .includes(search.replace(/\s/g, "").toLocaleLowerCase())
        : true;
    }

    return search !== ""
      ? user.some((i) => i.toLowerCase().includes(search.toLocaleLowerCase()))
      : true;
  });
};

export const filterAndSortLiveOrder = (
  orders: OrderV2[],
  status: OrderStatus[],
  search: string,
  sortValues,
  couriers: CmsUsersResponse[],
  pageValues: PaginationStatusProps,
  source: Sources[] | null
) => {
  const isArrived = status[0] === OrderStatus.ARRIVED;
  const page = pageValues[status[0]];

  return filterOrdersBySearch(
    orders?.filter((order) => {
      const isTableOrder =
        source === null
          ? true
          : source?.includes(Sources.QR)
            ? order.order_information.table_number !== undefined
            : source?.includes(Sources.POS)
              ? order.order_information.table_number === undefined &&
                source.includes(order.order_information.source)
              : source.includes(order.order_information.source);
      return (
        status.includes(order?.order_information?.order_status) && isTableOrder
      );
    }),
    search,
    couriers
  )
    .sort((a, b) => {
      const statusOfLane = status[0];
      const sortBy = sortValues[statusOfLane];

      if (sortBy === SortBy.PLACED_DATE_TIME) {
        const s1 = new Date(a.order_information.placed_date_time).getTime();
        const s2 = new Date(b.order_information.placed_date_time).getTime();
        return s2 - s1;
      } else if (sortBy === SortBy.HAND_OVER_TIME) {
        const s1 = new Date(a.order_information.hand_over_time).getTime();
        const s2 = new Date(b.order_information.hand_over_time).getTime();
        return s1 - s2;
      } else if (sortBy === SortBy.ZIP_CODE) {
        const s1 = getBaseZipcode(a.order_user.zip_code || "0000");
        const s2 = getBaseZipcode(b.order_user.zip_code || "0000");
        return parseInt(s2) - parseInt(s1);
      } else if (sortBy === SortBy.SOURCE) {
        const s1 = a.order_information.source;
        const s2 = b.order_information.source;
        return s1 - s2;
      } else if (sortBy === SortBy.STATUS) {
        const s1 = a.order_information.order_status;
        const s2 = b.order_information.order_status;
        return s1 - s2;
      }
    })
    .slice(
      0,
      (page + 1) *
        (isArrived ? MAX_ORDER_COUNT_FOR_ARRIVED : MAX_ORDER_COUNT_FOR_OTHERS)
    );
};

export const findZipcode = (
  search: string = "",
  zipcodes: ZipcodesTableDataRequest[]
) => {
  const [_zipcode, area] = search.split(",");
  return zipcodes
    ?.filter(
      (zipcode) =>
        _zipcode.toLowerCase() <= zipcode.zipcode_range_end.toLowerCase() &&
        _zipcode.toLowerCase() >= zipcode.zipcode_range_begin.toLowerCase()
    )
    .filter((zipcode) => {
      return area
        ? asArray(zipcode.area)
            .join()
            .toLowerCase()
            .includes(area?.toLowerCase())
        : true;
    });
};

export const sortOnHandOverTime = (orders: OrderV2[]) =>
  orders.sort(
    (a, b) =>
      new Date(a.order_information.hand_over_time).getTime() -
      new Date(b.order_information.hand_over_time).getTime()
  );

export const filterWithCouriers = (orders: OrderV2[], optional: boolean) => {
  return !optional
    ? orders.filter(
        (order) => order.order_information.assigned_courier_id === undefined
      )
    : orders;
};

export const sortOnDistance = (orders: OrderV2[]) =>
  orders.sort(
    (a, b) =>
      parseFloat(a.order_information.distance_to_branch_in_km) -
      parseFloat(b.order_information.distance_to_branch_in_km)
  );

export const sortOnReceiptNumber = (orders: OrderV2[]) =>
  orders.sort(
    (a, b) =>
      parseInt(a.order_information.daily_receipt_number) -
      parseInt(b.order_information.daily_receipt_number)
  );

export const generateGoogleUrl = (
  branch: Branch,
  orders: OrderV2[],
  sort = true
) => {
  const dir = (
    street: string,
    house_number: string,
    house_number_extension = "",
    city: string
  ) => `${street} ${house_number}${house_number_extension} ${city}`;
  const branchDir = encodeURIComponent(
    dir(
      branch.street_name,
      `${branch.house_number}`,
      branch.house_number_extension,
      branch.place
    )
  );
  const dirs = encodeURIComponent(
    orders
      .sort((a, b) =>
        sort
          ? parseFloat(a.order_information.distance_to_branch_in_km) -
            parseFloat(b.order_information.distance_to_branch_in_km)
          : 0
      )
      .map(({ order_user }) =>
        dir(
          order_user.street_name,
          order_user.house_number,
          order_user.house_number_extension,
          order_user.city
        )
      )
      .join("|")
  );

  return `https://www.google.com/maps/dir/?api=1&travelmode=driving&waypoints=${dirs}&destination=${branchDir}&waypoints=optimize:true`;
};

export const ordersToIds = (orders: OrderV2[]) =>
  orders.map((order) => order.order_information.item_id);

export function requestFullScreen(element) {
  // Supports most browsers and their versions.
  var requestMethod =
    element.requestFullScreen ||
    element.webkitRequestFullScreen ||
    element.mozRequestFullScreen ||
    element.msRequestFullScreen;

  if (requestMethod) {
    // Native full screen.
    requestMethod.call(element);
    // @ts-ignore
  } else if (typeof window.ActiveXObject !== "undefined") {
    // Older IE.
    // @ts-ignore
    var wscript = new ActiveXObject("WScript.Shell");
    if (wscript !== null) {
      wscript.SendKeys("{F11}");
    }
  }
}

export function numberToRGBA(num: number) {
  // Use a simple hash function to generate a hash from the number
  function hashCode(n) {
    let hash = 0;
    let str = n.toString();
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return hash;
  }

  // Generate a hash code for the given number
  const hash = hashCode(num);

  // Use the hash to generate RGBA values
  const r = (hash >> 24) & 0xff;
  const g = (hash >> 16) & 0xff;
  const b = (hash >> 8) & 0xff;
  const a = (hash & 0xff) / 255; // Alpha value between 0 and 1 with 2 decimal precision
  const value = [r, g, b, 255];
  return value;
}

export const summarizeDeposits = (numbers: Deposit) =>
  Object.values(numbers).reduce((p, v) => p + parseFloat(v), 0);

export const depositsToRows = (deposits: Deposit) =>
  Object.entries(deposits).map(([date, value], index) => ({
    item_id: index,
    date,
    value,
  }));

export const randomLiveInterval = () => {
  const oneMinuteInMilliseconds = 60 * 1000;
  const min = 2;
  const max = 5;

  return (
    oneMinuteInMilliseconds *
    (Math.floor(Math.random() * (max - min + 1)) + min)
  );
};

export const getLabel = (
  item: Product | Category | Garnish | Topping,
  isLowercase?: boolean,
  lang?: string
) => {
  const name = getFieldByLang(item.name, lang);
  const description = getFieldByLang(item.description, lang);
  const retVal = `${name}${description ? ` - ${description}` : ""}`;

  return isLowercase ? retVal.toLocaleLowerCase() : retVal;
};

export const includesSearch = (
  item: Product | Category | Garnish | Topping,
  search: string = "",
  lang?: string
) => {
  const price = item["price"] ? item["price"] : "";
  const label = getLabel(item, true, lang) + price;
  return label?.includes(search.toLocaleLowerCase());
};

export const isGreaterThanZero = (data: any[] = []) => data.length > 0;

export const reportToOverview = (report: CourierReport) => {
  const startMoney = {
    item_id: "money_at_start_of_day",
    description: "money_at_start_of_day",
    value: `${report.money_at_start_of_day}`,
  };
  const ordersToDescriptions = report.orders.map((order) => ({
    description: `${order.order_information.daily_receipt_number}`,
    value: order.order_information.total_price,
    date: order.order_information.placed_date_time,
    item_id: order.order_information.item_id,
    type: "order",
  }));
  const deposits = depositsToRows(report?.partial_deposit)?.map((row) => ({
    description: "deposit",
    value: `-${row.value}`,
    date: row.date,
    item_id: `${row.date}_deposit`,
  }));

  const o = [...ordersToDescriptions, ...deposits].sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
  );

  const end = {
    item_id: "owed_to_merchant",
    description: "owed_to_merchant",
    value: `${report.owed_to_merchant}`,
  };

  return [startMoney, ...o, end];
};

export const getDifferenceFromNow = (old: number) => {
  const now = Date.now();
  const differenceInMs = now - old;
  return Math.floor(differenceInMs / 1000);
};

export const ordersTotal = (orders: OrderV2[]) =>
  orders.reduce(
    (acc, cur) => (acc += parseFloat(cur.order_information.total_price)),
    0
  );

export const ordersTip = (orders: OrderV2[] = []) =>
  orders.reduce(
    (acc, cur) => acc + (parseFloat(cur?.order_information?.tip ?? "0") || 0),
    0
  );

export const totalKm = (orders: OrderV2[]) =>
  orders.reduce(
    (acc, cur) =>
      acc + parseFloat(cur?.order_information?.distance_to_branch_in_km ?? "0"),
    0
  );

export const controlFormContext = (formContext: UseFormMethods<any>) => {
  if (!formContext) {
    throw Error(
      "Unable to find FormContext. Please ensure that PriceWithTarget is used as a child of <FormProvider {...methods} />"
    );
  }
  return;
};

export const getBranchImageUrl = (branch: Branch) =>
  `https://res.cloudinary.com/appsmen-benelux-b-v/image/upload/order-website/branch/image/${
    branch?.item_id ?? branch?.id
  }/${branch?.image_cloudinary_id}`;

export const isHtml = (str: string): boolean => {
  const pattern = /(<([^>]+)>)/i;
  return pattern.test(str);
};

export const getCourierName = (
  order: OrderV2 | OrderInformation,
  couriers: CmsUsersResponse[]
) => {
  const { delivery_method_id, assigned_courier_id } =
    "order_information" in order ? order.order_information : order;
  const { DELIVERY } = DeliveryMethods;

  return (
    delivery_method_id === DELIVERY &&
    couriers.find((c) => c.item_id === assigned_courier_id)?.name
  );
};

export const hasSwitch = (order: OrderV2) => {
  const { EN_ROUTE, HANDED_OVER, READY_FOR_DELIVERY } = OrderStatus;
  const { TAKEAWAY, QR_TABLE_ORDER } = DeliveryMethods;
  const { delivery_method_id, order_status } = order.order_information;

  return (
    [EN_ROUTE, HANDED_OVER].includes(order_status) ||
    (delivery_method_id === TAKEAWAY && order_status === READY_FOR_DELIVERY) ||
    (delivery_method_id === QR_TABLE_ORDER &&
      order_status === READY_FOR_DELIVERY)
  );
};

export const cleanCharFromObject = (data: object, char?: string) => {
  return data !== undefined
    ? JSON.parse(JSON.stringify(data).replaceAll(char ?? "#", ""))
    : undefined;
};

export function isTimeBetween(date, start, end) {
  // Extract hours and minutes from the provided date
  const timeH = date.getHours();
  const timeM = date.getMinutes();
  const timeMinutes = timeH * 60 + timeM;

  // Convert start and end times to minutes
  const [startH, startM] = start.split(":").map(Number);
  const [endH, endM] = end.split(":").map(Number);

  const startMinutes = startH * 60 + startM;
  const endMinutes = endH * 60 + endM;

  return timeMinutes >= startMinutes && timeMinutes < endMinutes;
}

export const getOrderTrackerIconUrl = (branchId: number, id: string) =>
  `https://res.cloudinary.com/appsmen-benelux-b-v/image/upload/v1741265543/order-website/branch/tracker_icon/${branchId}/${id}.png`;

export const toFixed = (value: number, decimals: number = 3): number => {
  return parseFloat(value.toFixed(decimals));
};

export const getRandomNegativeId = () => Math.round(Math.random() * -100000);
export function chunkArray<T>(arr: T[], size: number): T[][] {
  return arr.reduce((chunks: T[][], item: T, index: number) => {
    const chunkIndex = Math.floor(index / size);
    if (!chunks[chunkIndex]) {
      chunks[chunkIndex] = [];
    }
    chunks[chunkIndex].push(item);
    return chunks;
  }, []);
}

export const filterSystemMessages = (messages: string[]) =>
  messages?.filter((m) => m !== "");

export const generateFlyerUrl = (flyerId: string, overlay?: string) => {
  if (overlay) {
    return `${
      import.meta.env.VITE_CLOUDINARY_BASE_URL
    }/${overlay}/${flyerId}.pdf`;
  }
  return `${import.meta.env.VITE_CLOUDINARY_BASE_URL}/${flyerId}.pdf`;
};

export const generateFlyerThumbnailUrl = (flyerId) =>
  `${
    import.meta.env.VITE_CLOUDINARY_BASE_URL
  }/c_thumb,w_75,g_face/${flyerId}.png`;

export const createPdfFile = (blob: BlobPart, name: string = "document.pdf") =>
  new File([blob], name, { type: "application/pdf" });

export async function promiseAllSequential<T>(
  tasks: (() => Promise<T>)[]
): Promise<T[]> {
  const results: T[] = [];
  for (const task of tasks) {
    results.push(await task());
  }
  return results;
}

type Item = {
  start_date?: string; // or Date if already parsed
  [key: string]: any;
};

export function sortByDate(items: Item[]): Item[] {
  const g = items.sort((a, b) => {
    const dateA = a.start_date ? new Date(a.start_date).getTime() : null;
    const dateB = b.start_date ? new Date(b.start_date).getTime() : null;
    if (dateA === null && dateB === null) return 0;
    if (dateA === null) return 1;
    if (dateB === null) return -1;
    return dateA - dateB;
  });
  return g;
}

export function isPhoneNumber(text: string) {
  const cleanedText = text?.replace(/\s/g, "");
  return (
    /^\d+$/.test(cleanedText) &&
    cleanedText?.length >= 8 &&
    cleanedText?.length <= 15
  );
}

export function splitAmount(amount, people) {
  const base = Math.floor((amount / people) * 100) / 100; // base share per person (rounded down to cents)
  const shares = new Array(people).fill(base);

  // Calculate remaining cents due to rounding
  const totalBase = base * people;
  const diff = Math.round((amount - totalBase) * 100); // in cents

  // Distribute remaining cents (at most 1 cent per person, starting with the first)
  for (let i = 0; i < diff; i++) {
    shares[i] += 0.01;
  }

  return shares;
}

export function makeOptions<T extends Record<string, any>>(
  list: T[] = [],
  value: keyof T = "item_id" as keyof T,
  label: keyof T = "name" as keyof T
) {
  return list.map((item) => ({ value: item[value], label: item[label] }));
}

export const getDayWage = (schedule: Schedule) => {
  const workedHours =
    new Date(schedule.end_date_and_time).getTime() -
    new Date(schedule.start_date_and_time).getTime();
  const workedTime = workedHours - parseInt(schedule.break_in_minutes) * 60000;
  const hourlyRate = schedule.hourly_wage ?? 0;

  return (workedTime / 3600000) * hourlyRate;
};

export const withCloudinaryImageSize = (
  image_cloudinary_id: string,
  width: number,
  height: number
) => {
  return (image_cloudinary_id || "")
    ?.replace("w_256", `w_${width}`)
    .replace("h_256", `h_${height}`);
};

export const withCloudinaryBackgroundRemoval = (
  image_cloudinary_id?: string | null
) => {
  if (!image_cloudinary_id) return "";
  if (!image_cloudinary_id.includes("/cms/")) {
    return image_cloudinary_id;
  }

  let url = image_cloudinary_id
    .replace(/,?b_rgb:[^,/?]+/g, "")
    .replace(/f_jpg/g, "f_png")
    .replace(/,{2,}/g, ",")
    .replace(/\/,/g, "/")
    .replace(/,\//g, "/");

  if (!url.includes("e_background_removal")) {
    url = url.replace("/cms/", "/e_background_removal/cms/");
  }

  return url;
};

export const isFavoriteCategory = (virtualItemType?: number) =>
  Number(virtualItemType) === CategoryType.FAVORITE;

export const sortFavoriteCategoriesFirst = (categories: readonly Category[]) =>
  [...categories].sort(
    (first, second) =>
      Number(isFavoriteCategory(second.virtual_item_type)) -
      Number(isFavoriteCategory(first.virtual_item_type))
  );
